import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { branchFinanceApi } from '../../services/branchFinanceApi';
import type {
    BranchOption,
    DepositSlip,
    DepositSlipListResponse,
} from '../../pages/BranchFinances/types/branchFinance.types';

interface DepositSlipFilters {
    branchId?: number;
    status?: number;
}

interface BranchDepositSlipsState {
    list: DepositSlip[];
    loadingList: boolean;
    error: string | null;
    branches: BranchOption[];
    filters: DepositSlipFilters;
    pagination: DepositSlipListResponse['meta'] | null;
    submitting: boolean;
    formError: string | null;
    approveLoadingId: number | null;
    rejectLoadingId: number | null;
}

const initialState: BranchDepositSlipsState = {
    list: [],
    loadingList: false,
    error: null,
    branches: [],
    filters: {},
    pagination: null,
    submitting: false,
    formError: null,
    approveLoadingId: null,
    rejectLoadingId: null,
};

export const fetchBranchDepositSlips = createAsyncThunk(
    'branchDepositSlips/fetchList',
    async (params: { branchId?: number; status?: number; page?: number; perPage?: number } = {}) => {
        return await branchFinanceApi.getDepositSlips(params);
    }
);

export const createBranchDepositSlip = createAsyncThunk(
    'branchDepositSlips/create',
    async (payload: { slipNo: string; amount: number; depositType: string; attachment: File }) => {
        return await branchFinanceApi.createDepositSlip({
            slip_no: payload.slipNo,
            amount: payload.amount,
            deposit_type: payload.depositType,
            attachment: payload.attachment,
        });
    }
);

export const updateBranchDepositSlip = createAsyncThunk(
    'branchDepositSlips/update',
    async (payload: {
        id: number;
        slipNo: string;
        amount: number;
        depositType: string;
        attachment?: File | null;
    }) => {
        return await branchFinanceApi.updateDepositSlip(payload.id, {
            slip_no: payload.slipNo,
            amount: payload.amount,
            deposit_type: payload.depositType,
            attachment: payload.attachment,
        });
    }
);

export const approveBranchDepositSlip = createAsyncThunk(
    'branchDepositSlips/approve',
    async (payload: { id: number; password: string }) => {
        return await branchFinanceApi.approveDepositSlip(payload.id, { pwd: payload.password });
    }
);

export const rejectBranchDepositSlip = createAsyncThunk(
    'branchDepositSlips/reject',
    async (payload: { id: number; reason: string; attachment?: File | null }) => {
        return await branchFinanceApi.rejectDepositSlip(payload.id, {
            reason: payload.reason,
            reason_attachment: payload.attachment,
        });
    }
);

const branchDepositSlipsSlice = createSlice({
    name: 'branchDepositSlips',
    initialState,
    reducers: {
        setDepositSlipFilters: (state, action: PayloadAction<DepositSlipFilters>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearDepositSlipError: (state) => {
            state.error = null;
            state.formError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBranchDepositSlips.pending, (state) => {
                state.loadingList = true;
                state.error = null;
            })
            .addCase(
                fetchBranchDepositSlips.fulfilled,
                (state, action: PayloadAction<DepositSlipListResponse>) => {
                    state.loadingList = false;
                    state.list = action.payload.data;
                    state.pagination = action.payload.meta;
                    if (action.payload.filters?.branches) {
                        state.branches = action.payload.filters.branches;
                    }
                }
            )
            .addCase(fetchBranchDepositSlips.rejected, (state, action) => {
                state.loadingList = false;
                state.error = action.error.message || 'Failed to load deposit slips';
            });

        builder
            .addCase(createBranchDepositSlip.pending, (state) => {
                state.submitting = true;
                state.formError = null;
            })
            .addCase(createBranchDepositSlip.fulfilled, (state, action: PayloadAction<DepositSlip>) => {
                state.submitting = false;
                state.list = [action.payload, ...state.list];
                if (state.pagination) {
                    state.pagination.total += 1;
                    const perPage = state.pagination.per_page;
                    if (perPage && state.list.length > perPage) {
                        state.list = state.list.slice(0, perPage);
                    }
                }
            })
            .addCase(createBranchDepositSlip.rejected, (state, action) => {
                state.submitting = false;
                state.formError = action.error.message || 'Failed to create deposit slip';
            });

        builder
            .addCase(updateBranchDepositSlip.pending, (state) => {
                state.submitting = true;
                state.formError = null;
            })
            .addCase(updateBranchDepositSlip.fulfilled, (state, action: PayloadAction<DepositSlip>) => {
                state.submitting = false;
                state.list = state.list.map((slip) => (slip.id === action.payload.id ? action.payload : slip));
            })
            .addCase(updateBranchDepositSlip.rejected, (state, action) => {
                state.submitting = false;
                state.formError = action.error.message || 'Failed to update deposit slip';
            });

        builder
            .addCase(approveBranchDepositSlip.pending, (state, action) => {
                state.approveLoadingId = action.meta.arg.id;
            })
            .addCase(approveBranchDepositSlip.fulfilled, (state, action: PayloadAction<DepositSlip>) => {
                state.approveLoadingId = null;
                state.list = state.list.map((slip) => (slip.id === action.payload.id ? action.payload : slip));
            })
            .addCase(approveBranchDepositSlip.rejected, (state) => {
                state.approveLoadingId = null;
            });

        builder
            .addCase(rejectBranchDepositSlip.pending, (state, action) => {
                state.rejectLoadingId = action.meta.arg.id;
            })
            .addCase(rejectBranchDepositSlip.fulfilled, (state, action: PayloadAction<DepositSlip>) => {
                state.rejectLoadingId = null;
                state.list = state.list.map((slip) => (slip.id === action.payload.id ? action.payload : slip));
            })
            .addCase(rejectBranchDepositSlip.rejected, (state) => {
                state.rejectLoadingId = null;
            });
    },
});

export const { setDepositSlipFilters, clearDepositSlipError } = branchDepositSlipsSlice.actions;
export default branchDepositSlipsSlice.reducer;



